import { useState } from "react";
import { Box, Button, Divider, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PublicIcon from "@mui/icons-material/Public";
import CloseIcon from "@mui/icons-material/Close";
import CheckListOptions from "./CheckListOptions";
import RangeSlider from "./RangeSlider";
import BuildersInput from "./BuildersInput";
import OrderByOptions from "./OrderByOptions";
import { useCumulativeCounts } from "../../utils/useCumulativeCounts";

interface FiltersWidgetProps {
  prices: number[];
  priceMin?: number;
  priceMax?: number;
  yearMin?: number;
  yearMax?: number;
  lengthMin?: number;
  lengthMax?: number;
  years: any[];
  lengths: number[];
  boatsFilter: string[];
  setBoatsFilter: React.Dispatch<React.SetStateAction<string[]>>;
  cabinsFilter: string[];
  setCabinsFilter: React.Dispatch<React.SetStateAction<string[]>>;
  bathroomFilters: string[];
  setBathroomFilter: React.Dispatch<React.SetStateAction<string[]>>;
  priceFilter: number[];
  setPriceFilter: (value: number[]) => void;
  priceUserSet?: boolean;
  yearFilter: number[];
  setyearFilter: (value: number[]) => void;
  yearUserSet?: boolean;
  lengthFilter: number[];
  setLengthFilter: (value: number[]) => void;
  lengthUserSet?: boolean;
  amenitiesFilter: string[];
  setAmenitiesFilter: React.Dispatch<React.SetStateAction<string[]>>;
  orderFilter: string;
  setOrderFilter: React.Dispatch<React.SetStateAction<string>>;
  buildersInput: string[];
  setBuildersInput: React.Dispatch<React.SetStateAction<string[]>>;
  yachtModels: string[];
  clusterYachts: any[];
  setClusterYachts: React.Dispatch<React.SetStateAction<any[]>>;
}

const AllFiltersBox = ({
  prices,
  priceMin = 552,
  priceMax = 61658,
  yearMin = 1990,
  yearMax = 2025,
  lengthMin = 20,
  lengthMax = 120,
  years,
  lengths,
  boatsFilter,
  setBoatsFilter,
  cabinsFilter,
  setCabinsFilter,
  bathroomFilters,
  setBathroomFilter,
  priceFilter,
  setPriceFilter,
  priceUserSet,
  yearFilter,
  setyearFilter,
  yearUserSet,
  lengthFilter,
  setLengthFilter,
  lengthUserSet,
  amenitiesFilter,
  setAmenitiesFilter,
  orderFilter,
  setOrderFilter,
  buildersInput,
  setBuildersInput,
  yachtModels,
  clusterYachts,
  setClusterYachts,
}: FiltersWidgetProps) => {
  const { counts, amenities } = useCumulativeCounts();

  // Auto-expand sections that have an active filter when the panel opens.
  // Since AllFiltersBox is conditionally rendered, this useState runs fresh each open.
  const [open, setOpen] = useState<Record<string, boolean>>({
    boat_type: boatsFilter.length > 0,
    cabins: cabinsFilter.length > 0,
    bathrooms: bathroomFilters.length > 0,
    price: !!priceUserSet,
    year: !!yearUserSet,
    length: !!lengthUserSet,
    equipment: amenitiesFilter.length > 0,
    model: buildersInput.length > 0,
    order: orderFilter !== '',
  });

  const toggle = (key: string) =>
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const Section = ({
    id,
    title,
    children,
  }: {
    id: string;
    title: string;
    children: React.ReactNode;
  }) => (
    <Box>
      <Box
        onClick={() => toggle(id)}
        sx={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.5,
          userSelect: "none",
        }}
      >
        <Typography fontWeight="bold" fontSize="14px">
          {title}
        </Typography>
        <ExpandMoreIcon
          sx={{
            fontSize: 20,
            color: "gray",
            transition: "transform 0.2s",
            transform: open[id] ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>
      {open[id] && <Box pb={2}>{children}</Box>}
      <Divider />
    </Box>
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      margin="auto"
      width="100%"
      maxWidth="350px"
    >
      {clusterYachts?.length > 0 && (
        <Box pb={1.5}>
          <Button
            variant="outlined"
            startIcon={<PublicIcon />}
            endIcon={<CloseIcon />}
            onClick={() => setClusterYachts([])}
            sx={{
              borderRadius: '9999px',
              textTransform: 'none',
              backgroundColor: '#f5f5f5',
              color: '#333',
              borderColor: '#ccc',
              paddingX: 2,
              paddingY: 0.5,
              fontSize: '0.875rem',
              '&:hover': { backgroundColor: '#e0e0e0', borderColor: '#bbb' },
            }}
          >
            Map filter active — tap to clear
          </Button>
        </Box>
      )}
      <Section id="boat_type" title="Boat Type">
        <CheckListOptions
          selectedFilters={boatsFilter}
          setSelectedFilters={setBoatsFilter}
          options={(counts.yacht_types ?? []).map(({ name, count }) => ({
            label: name,
            count,
          }))}
        />
      </Section>

      <Section id="cabins" title="Cabins">
        <CheckListOptions
          selectedFilters={cabinsFilter}
          setSelectedFilters={setCabinsFilter}
          options={(counts.cabins ?? []).map(({ name, count }) => ({
            label: name,
            count,
          }))}
        />
      </Section>

      <Section id="bathrooms" title="Bathrooms">
        <CheckListOptions
          selectedFilters={bathroomFilters}
          setSelectedFilters={setBathroomFilter}
          options={(counts?.bathrooms ?? []).map(({ name, count }) => ({
            label: name,
            count,
          }))}
        />
      </Section>

      <Section id="price" title="Price">
        <RangeSlider
          title="Price"
          min={priceMin}
          max={priceMax}
          range={priceFilter}
          setRange={setPriceFilter}
          histogramValues={prices}
        />
      </Section>

      <Section id="year" title="Year">
        <RangeSlider
          title="Year"
          min={yearMin}
          max={yearMax}
          range={yearFilter}
          setRange={setyearFilter}
          histogramValues={years}
        />
      </Section>

      <Section id="length" title="Length">
        <RangeSlider
          title="Length"
          min={lengthMin}
          max={lengthMax}
          range={lengthFilter}
          setRange={setLengthFilter}
          histogramValues={lengths}
        />
      </Section>

      <Section id="model" title="Boat Model">
        <BuildersInput
          options={yachtModels}
          selectedFilter={buildersInput}
          setSelectedFilter={setBuildersInput}
        />
      </Section>

      <Section id="equipment" title="Equipment">
        <CheckListOptions
          selectedFilters={amenitiesFilter}
          setSelectedFilters={setAmenitiesFilter}
          options={[
            { label: "Furling Mainsail" },
            ...[...amenities].filter(a => a.label.toLowerCase() !== 'furling mainsail' && !a.label.toLowerCase().includes('unknown')),
          ].sort((a, b) => a.label.localeCompare(b.label))}
        />
      </Section>

      <Section id="order" title="Order by">
        <OrderByOptions
          selectedOption={orderFilter}
          setSelectedOption={setOrderFilter}
          options={[
            "Lowest price",
            "Lowest cabins",
            "Highest discount",
            "Increasing Length",
          ]}
        />
      </Section>
    </Box>
  );
};

export default AllFiltersBox;
